import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, signal, HostListener, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EstoqueService } from '../../services/estoque.service';
import { ProdutoEstoque, LocalEstoque, LocalProduto, ProdutoBusca, TransferenciaPayload } from '../../models/estoque.model';
import { SankhyaGatewayService } from 'src/app/services/sankhya-gateway.service';
import { EmpresaStateService } from '../../services/empresa-state.service';

@Component({
  selector: 'app-gpl-transferencia',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './gpl-transferencia.component.html',
  styleUrl: './gpl-transferencia.component.css'
})
export class GplTransferenciaComponent implements OnInit, OnDestroy {
  loading = signal(false);
  loadingMsg = signal('');
  
  // Locations lists
  locais: LocalEstoque[] = [];
  locaisDestino: LocalEstoque[] = [];
  
  // Selected locations
  codLocalOrigem = signal<number | null>(null);
  codLocalDestino = signal<number | null>(null);
  
  // Custom dropdown display states
  dropOrigem = signal(false);
  dropDestino = signal(false);
  
  // Dropdowns filters
  filtroOrigem = '';
  filtroDestino = '';
  
  // Products list
  produtos = signal<ProdutoEstoque[]>([]);
  selecionarTodos = false;
  
  // Confirmation Modal
  mostrarModalConfirm = signal(false);
  
  // Advanced settings card collapsible
  mostrarConfigAvancada = signal(false);

  // Advanced parameters defaults
  codTipOper = 1950;
  serieNota = '1';
  codParc = 1;
  codNat = 3010101;
  codCenCus = 0;
  codTipVenda = 0;

  // Alerts & Messages
  alertaModal = signal(false);
  alertaTexto = signal('');
  alertaTipo = signal<'sucesso' | 'erro' | 'aviso'>('aviso');
  
  // Transfer execution result
  nuNotaCriada = signal<number | null>(null);
  resultado = signal<{ nuNota?: number } | null>(null);

  // Gallery Modal
  modalGaleria = signal(false);
  galeriaCarregando = signal(false);
  galeriaDescricao = '';
  listaImagens: string[] = [];
  indexImagem = 0;
  imagemAtual: string | null = null;

  // Preview Floating Box
  previewVisible = signal(false);
  previewSrc = '';
  previewTop = '0px';
  previewLeft = '0px';
  private slideTimer: any = null;
  private slideImgs: string[] = [];
  private slideIdx = 0;

  // Quick Search for Product
  termoBusca = '';
  resultadosBusca = signal<ProdutoBusca[]>([]);
  buscando = signal(false);
  locaisProduto = signal<LocalProduto[]>([]);
  produtoSelecionado: ProdutoBusca | null = null;
  mostrarLocaisProduto = signal(false);
  private debounceTimer: any = null;

  private _empresaSub: { unsubscribe(): void } | null = null;

  constructor(
    private readonly estoqueService: EstoqueService,
    private readonly sankhyaService: SankhyaGatewayService,
    readonly empresaState: EmpresaStateService,
  ) {}

  /** Empresa (CODEMP real) atualmente selecionada no seletor global (barra
   *  lateral) -- usada tanto pra filtrar os Locais disponíveis quanto pra
   *  gravar na nota de transferência. Ver EmpresaStateService.codEmpReal. */
  get codEmpAtual(): number {
    return Number(this.empresaState.codEmpReal || this.empresaState.selecionada || 1);
  }

  /** Locais visíveis pra seleção (origem/destino) na empresa atual: um local
   *  só aparece aqui se ainda não tem empresa "definida" (nunca teve estoque
   *  lançado) ou se já pertence à empresa selecionada -- ver comentário em
   *  LocalRepository.buscarLocais() no addon. Isso evita o usuário escolher
   *  um local que na prática é usado por outra empresa. */
  private locaisDaEmpresaAtual(): LocalEstoque[] {
    const emp = this.codEmpAtual;
    return this.locais.filter((l) => l.codEmp == null || Number(l.codEmp) === emp);
  }

  /** Mesma checagem de `locaisDaEmpresaAtual()`, mas por CODLOCAL avulso --
   *  usada pelos fluxos de "Busca Rápida por Produto" (abaixo), que não
   *  passam pelos dropdowns de Origem/Destino e por isso precisam da própria
   *  validação antes de selecionar um local como origem. */
  private localPertenceEmpresaAtual(codLocal: number): boolean {
    const loc = this.locais.find((l) => l.codLocal === codLocal);
    return !loc || loc.codEmp == null || Number(loc.codEmp) === this.codEmpAtual;
  }

  /** digitsInfo do pipe "number" para qualquer campo de quantidade/estoque com
   *  base na unidade do produto (mesma lógica usada em Pedido de Venda,
   *  Consulta de Peças e Pedido de Compra). UN nunca tem fração (não dá pra
   *  transferir "meia peça"), então mostra inteiro; unidades fracionárias
   *  (M, KG, L, etc.) continuam mostrando até 3 casas decimais. */
  casasDecimaisQtd(unidade: string | null | undefined, valor?: number | string | null): string {
    // "valor" às vezes chega como string (ex.: BigDecimal serializado como "2.000"
    // pelo backend) em vez de number — Number.isInteger('2.000') sempre retorna
    // false para string, mesmo o valor sendo inteiro, então isso derrubava pro
    // fallback por unidade e voltava a mostrar ".000". Convertendo com Number(...)
    // e tolerando resíduo de ponto flutuante (ex.: 1.9999999999998 vindo de SUM()
    // no Oracle) cobre os dois casos.
    if (valor != null && valor !== '') {
      const num = typeof valor === 'string' ? Number(valor.replace(',', '.')) : valor;
      if (Number.isFinite(num) && Math.abs(num - Math.round(num)) < 1e-6) {
        return '1.0-0';
      }
    }
    const un = String(unidade ?? 'UN').trim().toUpperCase();
    return un === 'UN' ? '1.0-0' : '1.0-3';
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.dropOrigem.set(false);
    this.dropDestino.set(false);
  }

  toggleDrop(tipo: 'origem' | 'destino'): void {
    if (tipo === 'origem') {
      this.dropOrigem.set(!this.dropOrigem());
      this.dropDestino.set(false);
      if (this.dropOrigem()) {
        this.filtroOrigem = '';
      }
    } else {
      this.dropDestino.set(!this.dropDestino());
      this.dropOrigem.set(false);
      if (this.dropDestino()) {
        this.filtroDestino = '';
      }
    }
  }

  nomeLocalOrigem(): string {
    const orig = this.codLocalOrigem();
    if (!orig) return '';
    const loc = this.locais.find((l) => l.codLocal === orig);
    return loc ? `${loc.codLocal} - ${loc.descricao}` : '';
  }

  nomeLocalDestino(): string {
    const dest = this.codLocalDestino();
    if (!dest) return '';
    const loc = this.locais.find((l) => l.codLocal === dest);
    return loc ? `${loc.codLocal} - ${loc.descricao}` : '';
  }

  onTermoChange(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.resultadosBusca.set([]);

    if (!this.termoBusca || this.termoBusca.trim().length < 3) {
      return;
    }

    this.buscando.set(true);
    this.debounceTimer = setTimeout(() => {
      this.executarBusca(this.termoBusca.trim());
    }, 400);
  }

  executarBusca(termo: string): void {
    this.estoqueService.buscarProdutos(termo).subscribe({
      next: (res: any) => {
        this.buscando.set(false);
        if (!res) {
          this.resultadosBusca.set([]);
          return;
        }
        const body = res.responseBody;
        if (res.status === '1' && body && body.lista) {
          this.resultadosBusca.set(body.lista);
        } else {
          this.resultadosBusca.set([]);
        }
      },
      error: (err) => {
        this.buscando.set(false);
        this.resultadosBusca.set([]);
        this.abrirAlerta('Falha na busca: ' + err.message, 'erro');
      }
    });
  }

  selecionarProdutoBusca(prod: ProdutoBusca): void {
    this.produtoSelecionado = prod;
    this.termoBusca = prod.descricao || prod.nome || String(prod.codprod || prod.codigo || '');
    this.resultadosBusca.set([]);
    this.buscando.set(true);

    const codprod = Number(prod.codprod || prod.codigo || 0);

    // Já pede pro backend filtrar pela empresa atual (TGFEST.CODEMP) -- evita
    // nem carregar/listar locais de outras empresas nessa busca (pedido do
    // usuário: "não deveria nem carregar locais de produtos de outra empresa").
    this.estoqueService.buscarLocaisDoProduto(codprod, this.codEmpAtual).subscribe({
      next: (res) => {
        this.buscando.set(false);
        if (!res) {
          this.abrirAlerta('Nenhum resultado retornado pelo servidor', 'erro');
          return;
        }
        const body = res.responseBody;
        if (res.status === '1' && body && body.sucesso) {
          const locais = body.locais || [];
          if (locais.length === 0) {
            this.abrirAlerta('Produto sem estoque em nenhum local da empresa atual (' + this.codEmpAtual + ')', 'aviso');
          } else if (locais.length === 1) {
            const codLocalUnico = Number(locais[0].codLocal);
            if (!this.localPertenceEmpresaAtual(codLocalUnico)) {
              this.limparBusca();
              this.abrirAlerta('Esse produto só tem estoque no local ' + locais[0].codLocal + ' - ' + locais[0].descrLocal + ', que pertence a outra empresa. Troque a empresa selecionada (barra lateral) para transferir a partir dele.', 'aviso');
              return;
            }
            this.codLocalOrigem.set(codLocalUnico);
            this.limparBusca();
            this.onLocalOrigemChange();
            this.abrirAlerta('Local ' + locais[0].codLocal + ' - ' + locais[0].descrLocal + ' selecionado!', 'sucesso');
          } else {
            this.locaisProduto.set(locais);
            this.mostrarLocaisProduto.set(true);
          }
        } else {
          const errMsg = body?.erro || res?.statusMessage || 'Erro desconhecido';
          this.abrirAlerta('Erro ao buscar locais: ' + errMsg, 'erro');
        }
      },
      error: (err) => {
        this.buscando.set(false);
        this.abrirAlerta('Erro ao buscar locais: ' + err.message, 'erro');
      }
    });
  }

  selecionarLocalDoProduto(loc: LocalProduto): void {
    const codLocal = Number(loc.codLocal);
    if (!this.localPertenceEmpresaAtual(codLocal)) {
      this.abrirAlerta('O local ' + loc.codLocal + ' - ' + loc.descrLocal + ' pertence a outra empresa. Troque a empresa selecionada (barra lateral) para transferir a partir dele.', 'aviso');
      return;
    }
    this.codLocalOrigem.set(codLocal);
    this.mostrarLocaisProduto.set(false);
    this.locaisProduto.set([]);
    this.limparBusca();
    this.onLocalOrigemChange();
    this.abrirAlerta('Local ' + loc.codLocal + ' - ' + loc.descrLocal + ' selecionado!', 'sucesso');
  }

  limparBusca(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.termoBusca = '';
    this.resultadosBusca.set([]);
    this.buscando.set(false);
    this.locaisProduto.set([]);
    this.mostrarLocaisProduto.set(false);
    this.produtoSelecionado = null;
  }

  ngOnInit(): void {
    // Garante que a empresa global já esteja carregada (idempotente -- se outra
    // tela já carregou antes, isso é um no-op) antes de buscar os locais, já
    // que a lista exibida depende de saber qual é a empresa atual.
    this.empresaState.carregar().finally(() => {
      this.carregarLocaisStock();
    });

    // Troca de empresa (feita no seletor da barra lateral) enquanto esta tela
    // está aberta: reaplica o filtro e, se a origem/destino selecionada não
    // pertencer mais à empresa nova, limpa a seleção pra evitar transferir
    // entre locais de empresas diferentes.
    this._empresaSub = this.empresaState.empresaMudou.subscribe(() => {
      this.aoTrocarEmpresa();
    });
  }

  ngOnDestroy(): void {
    this._empresaSub?.unsubscribe();
  }

  private aoTrocarEmpresa(): void {
    const emp = this.codEmpAtual;
    const origem = this.codLocalOrigem();
    const destino = this.codLocalDestino();

    const origemLoc = origem ? this.locais.find((l) => l.codLocal === origem) : null;
    const origemInvalida = !!origemLoc && origemLoc.codEmp != null && Number(origemLoc.codEmp) !== emp;

    const destinoLoc = destino ? this.locais.find((l) => l.codLocal === destino) : null;
    const destinoInvalido = !!destinoLoc && destinoLoc.codEmp != null && Number(destinoLoc.codEmp) !== emp;

    if (origemInvalida) {
      this.abrirAlerta('A empresa foi trocada -- o local de origem selecionado pertence a outra empresa e foi limpo.', 'aviso');
      this.codLocalOrigem.set(null);
      this.onLocalOrigemChange();
      return; // onLocalOrigemChange() já recalcula locaisDestino
    }

    if (destinoInvalido) {
      this.abrirAlerta('A empresa foi trocada -- o local de destino selecionado pertence a outra empresa e foi limpo.', 'aviso');
      this.codLocalDestino.set(null);
    }

    // Reaplica o filtro dos dropdowns mesmo sem limpar nada (ex.: locais que
    // passaram a ficar disponíveis/indisponíveis com a nova empresa).
    this.locaisDestino = this.locaisDaEmpresaAtual().filter((l) => l.codLocal !== this.codLocalOrigem());
  }

  carregarLocaisStock(): void {
    this.loading.set(true);
    this.loadingMsg.set('Carregando locais de estoque...');
    this.estoqueService.buscarLocais().subscribe({
      next: (res) => {
        this.loading.set(false);
        if (!res) {
          this.abrirAlerta('Nenhum local retornado pelo servidor', 'erro');
          return;
        }
        const body = res.responseBody;
        if (res.status === '1' && body && body.sucesso && body.locais) {
          this.locais = body.locais;
          this.locaisDestino = this.locaisDaEmpresaAtual();
        } else {
          const errMsg = body?.erro || res?.statusMessage || 'Erro desconhecido';
          this.abrirAlerta('Erro ao carregar locais: ' + errMsg, 'erro');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.abrirAlerta('Falha na comunicação ao carregar locais: ' + err.message, 'erro');
      }
    });
  }

  selecionarLocal(tipo: 'origem' | 'destino', codLocal: number | null): void {
    if (tipo === 'origem') {
      this.codLocalOrigem.set(codLocal);
      this.dropOrigem.set(false);
      this.onLocalOrigemChange();
    } else {
      this.codLocalDestino.set(codLocal);
      this.dropDestino.set(false);
    }
  }

  onLocalOrigemChange(): void {
    this.produtos.set([]);
    this.nuNotaCriada.set(null);
    this.resultado.set(null);
    this.selecionarTodos = false;
    const origin = this.codLocalOrigem();

    if (!origin) {
      this.locaisDestino = this.locaisDaEmpresaAtual();
      return;
    }

    // Filter destination options to omit selected origin (e já restrito à empresa atual)
    this.locaisDestino = this.locaisDaEmpresaAtual().filter((loc) => loc.codLocal !== origin);

    // Reset destination if equal to origin
    if (this.codLocalDestino() === origin) {
      this.codLocalDestino.set(null);
    }

    this.loading.set(true);
    this.loadingMsg.set('Buscando produtos no local...');
    this.estoqueService.buscarProdutosPorLocal(origin).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (!res) {
          this.produtos.set([]);
          return;
        }
        const body = res.responseBody;
        if (res.status === '1' && body && body.sucesso && body.produtos) {
          const list = body.produtos.map((p) => {
            p.selecionado = false;
            p.qtdTransferir = p.estoque;
            p.imagemSrc = p.imagem
              ? 'data:image/jpeg;base64,' + p.imagem
              : this.gerarPlaceholder(p.descricao || 'PRO');
            return p;
          });
          this.produtos.set(list);
        } else {
          const errMsg = body?.erro || res?.statusMessage || 'Erro desconhecido';
          this.abrirAlerta('Erro ao buscar produtos: ' + errMsg, 'erro');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.abrirAlerta('Falha ao buscar produtos: ' + err.message, 'erro');
      }
    });
  }

  itensSelecionados(): ProdutoEstoque[] {
    return this.produtos().filter((p) => p.selecionado && p.qtdTransferir && p.qtdTransferir > 0);
  }

  toggleTodos(): void {
    const list = this.produtos().map((p) => {
      p.selecionado = this.selecionarTodos;
      return p;
    });
    this.produtos.set(list);
  }

  limparSelecao(): void {
    this.selecionarTodos = false;
    const list = this.produtos().map((p) => {
      p.selecionado = false;
      p.qtdTransferir = p.estoque;
      return p;
    });
    this.produtos.set(list);
    this.nuNotaCriada.set(null);
  }

  nomeLocal(codLocal: number | null): string {
    if (!codLocal) return '';
    const loc = this.locais.find((l) => l.codLocal === codLocal);
    return loc ? `${loc.codLocal} - ${loc.descricao}` : '';
  }

  locaisOrigemFiltrados(): LocalEstoque[] {
    const base = this.locaisDaEmpresaAtual();
    const term = this.filtroOrigem.trim().toLowerCase();
    if (!term) return base;
    return base.filter(l =>
      String(l.codLocal).includes(term) || l.descricao.toLowerCase().includes(term)
    );
  }

  locaisDestinoFiltrados(): LocalEstoque[] {
    const term = this.filtroDestino.trim().toLowerCase();
    if (!term) return this.locaisDestino;
    return this.locaisDestino.filter(l => 
      String(l.codLocal).includes(term) || l.descricao.toLowerCase().includes(term)
    );
  }

  abrirAlerta(texto: string, tipo: 'sucesso' | 'erro' | 'aviso' = 'aviso'): void {
    this.alertaTexto.set(texto);
    this.alertaTipo.set(tipo);
    this.alertaModal.set(true);
  }

  fecharAlerta(): void {
    this.alertaModal.set(false);
  }

  validarTransferencia(): void {
    const origin = this.codLocalOrigem();
    const dest = this.codLocalDestino();
    
    if (!origin) {
      this.abrirAlerta('Selecione o local de origem', 'aviso');
      return;
    }
    if (!dest) {
      this.abrirAlerta('Selecione o local de destino', 'aviso');
      return;
    }
    if (origin === dest) {
      this.abrirAlerta('O local de origem e destino não podem ser iguais', 'aviso');
      return;
    }

    const selecionados = this.itensSelecionados();
    if (selecionados.length === 0) {
      this.abrirAlerta('Selecione ao menos um produto para transferir', 'aviso');
      return;
    }

    // Validate quantities
    for (const item of selecionados) {
      const qty = item.qtdTransferir || 0;
      if (qty <= 0) {
        this.abrirAlerta(`Produto ${item.codProd} possui quantidade inválida para transferir`, 'aviso');
        return;
      }
      if (qty > item.estoque) {
        this.abrirAlerta(`Produto ${item.codProd} - Quantidade a transferir (${qty}) excede o estoque disponível (${item.estoque})`, 'erro');
        return;
      }
    }

    this.mostrarModalConfirm.set(true);
  }

  cancelarTransferencia(): void {
    this.mostrarModalConfirm.set(false);
  }

  executarTransferencia(): void {
    this.mostrarModalConfirm.set(false);
    this.loading.set(true);
    this.loadingMsg.set('Gerando notas de transferência...');

    const selecionados = this.itensSelecionados();
    const itensPayload = selecionados.map((p) => ({
      codProd: p.codProd,
      qtd: p.qtdTransferir || 0,
      codVol: p.codVol,
      controle: p.controle || ''
    }));

    const payload: TransferenciaPayload = {
      CODEMP: this.codEmpAtual,
      CODLOCALORIGEM: this.codLocalOrigem() || 0,
      CODLOCALDESTINO: this.codLocalDestino() || 0,
      CODTIPOPER: this.codTipOper,
      SERIENOTA: this.serieNota,
      CODPARC: this.codParc,
      CODNAT: this.codNat,
      CODCENCUS: this.codCenCus,
      CODTIPVENDA: this.codTipVenda,
      itens: itensPayload
    };

    this.estoqueService.transferir(payload).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (!res) {
          this.abrirAlerta('Nenhum resultado retornado pelo servidor', 'erro');
          return;
        }
        const body = res.responseBody;
        if (res.status === '1' && body && body.sucesso) {
          this.nuNotaCriada.set(body.nuNota || null);
          this.resultado.set({ nuNota: body.nuNota });
          this.abrirAlerta(body.mensagem || `Transferência realizada com sucesso! Nota fiscal gerada: ${body.nuNota}`, 'sucesso');
          
          // Reload origin stock items
          this.onLocalOrigemChange();
        } else {
          const errMsg = body?.erro || res?.statusMessage || 'Erro desconhecido';
          this.abrirAlerta('Erro na transferência: ' + errMsg, 'erro');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.abrirAlerta('Falha ao processar transferência: ' + err.message, 'erro');
      }
    });
  }

  // ============================
  // FULLSCREEN IMAGE GALLERY
  // ============================
  abrirGaleria(prod: ProdutoEstoque): void {
    this.hidePreview();
    this.modalGaleria.set(true);
    this.galeriaCarregando.set(true);
    this.galeriaDescricao = prod.descricao || '';
    
    // Add default preview thumbnail
    this.listaImagens = [prod.imagemSrc || ''];
    this.indexImagem = 0;
    this.imagemAtual = this.listaImagens[0];

    this.estoqueService.buscarImgProduto(prod.codProd).subscribe({
      next: (res) => {
        this.galeriaCarregando.set(false);
        const body = res.responseBody;
        const imgs: string[] = [];
        
        if (body && body.principal) {
          imgs.push('data:image/jpeg;base64,' + body.principal);
        } else {
          imgs.push(prod.imagemSrc || '');
        }

        if (body && body.lista) {
          body.lista.forEach((item) => {
            if (item.imagem) {
              imgs.push('data:image/jpeg;base64,' + item.imagem);
            }
          });
        }
        
        this.listaImagens = imgs;
        this.indexImagem = 0;
        this.imagemAtual = imgs[0];
      },
      error: () => {
        this.galeriaCarregando.set(false);
      }
    });
  }

  proximaImagem(): void {
    if (this.indexImagem < this.listaImagens.length - 1) {
      this.indexImagem++;
      this.imagemAtual = this.listaImagens[this.indexImagem];
    }
  }

  imagemAnterior(): void {
    if (this.indexImagem > 0) {
      this.indexImagem--;
      this.imagemAtual = this.listaImagens[this.indexImagem];
    }
  }

  fecharGaleria(): void {
    this.modalGaleria.set(false);
  }

  // ============================
  // FLOATING HOVER PREVIEW
  // ============================
  showPreview(event: MouseEvent, src: string, codProd: number): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    this.previewSrc = src;
    this.previewVisible.set(true);
    
    // Position calculation
    const rect = target.getBoundingClientRect();
    const size = 280;
    let top = rect.top + window.scrollY - size / 2 + rect.height / 2;
    let left = rect.right + window.scrollX + 12;

    if (left + size + 16 > window.innerWidth) {
      left = rect.left + window.scrollX - size - 12;
    }
    if (top + size > window.scrollY + window.innerHeight - 8) {
      top = window.scrollY + window.innerHeight - size - 8;
    }
    if (top < window.scrollY + 8) {
      top = window.scrollY + 8;
    }

    this.previewTop = `${top}px`;
    this.previewLeft = `${left}px`;
    
    this.slideImgs = [src];
    this.slideIdx = 0;
    clearInterval(this.slideTimer);

    // Fetch addition images for auto slideshow
    this.estoqueService.buscarImgProduto(codProd).subscribe({
      next: (res) => {
        const body = res.responseBody;
        const imgs: string[] = [];
        if (body && body.principal) imgs.push('data:image/jpeg;base64,' + body.principal);
        else imgs.push(src);

        if (body && body.lista) {
          body.lista.forEach((i) => {
            if (i.imagem) imgs.push('data:image/jpeg;base64,' + i.imagem);
          });
        }
        
        this.slideImgs = imgs;
        if (imgs.length > 1) {
          this.slideTimer = setInterval(() => {
            if (!this.previewVisible()) {
              clearInterval(this.slideTimer);
              return;
            }
            this.slideIdx = (this.slideIdx + 1) % this.slideImgs.length;
            this.previewSrc = this.slideImgs[this.slideIdx];
          }, 1500);
        }
      }
    });
  }

  hidePreview(): void {
    clearInterval(this.slideTimer);
    this.slideImgs = [];
    this.slideIdx = 0;
    this.previewVisible.set(false);
  }

  // ============================
  // PLACEHOLDER CANVAS CREATOR
  // ============================
  private gerarPlaceholder(texto: string): string {
    const letters = texto.substring(0, 3).toUpperCase();
    const colors = ["#1abc9c", "#2ecc71", "#3498db", "#9b59b6", "#e67e22", "#e74c3c", "#34495e"];
    const bgColor = colors[letters.charCodeAt(0) % colors.length];
    
    const size = 200;
    const radius = 30;
    
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(size - radius, 0);
      ctx.quadraticCurveTo(size, 0, size, radius);
      ctx.lineTo(size, size - radius);
      ctx.quadraticCurveTo(size, size, size - radius, size);
      ctx.lineTo(radius, size);
      ctx.quadraticCurveTo(0, size, 0, size - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();
      
      ctx.fillStyle = bgColor;
      ctx.fill();
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 70px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(letters, size / 2, size / 2);
    }
    
    return canvas.toDataURL('image/png');
  }
}
